"use client"

import { usePathname } from "next/navigation"
import { CopyIcon } from "@radix-ui/react-icons"
import { Loader2Icon } from "lucide-react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { FormLabel } from "@/components/ui/form"
import CopyButton from "@/components/copy-button"
import SocialMediaShare from "@/components/generators/_components/SocialMediaShare"
import { useAdditionalTranslations } from "@/components/providers"

const baseUrl =
  process.env.NEXT_PUBLIC_APP_URL ?? "https://www.uniquenamegenerators.com"

type VideoGameNameGeneratorResultsProps = {
  isLoading: boolean
  completion: string
}

const parseCompletion = (completion: string): string[] => {
  return completion
    .split(",")
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
}

// Later every Generator should have its own Result Page.
export default function AllGeneratorResults({
  isLoading,
  completion,
}: VideoGameNameGeneratorResultsProps) {
  const { translatedSubject } = useAdditionalTranslations()
  const t = useTranslations("Generator.Shared")
  const currentPath = usePathname()
  // construct the url for sharing, avoiding double slashes
  const url = `${baseUrl.replace(/\/$/, "")}${currentPath}`
  const names = parseCompletion(completion)
  const boxCount = 10 // Number of boxes to show

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(names.join("\n"))
      toast.info(`${names.length} names copied!`)
    } catch (err) {
      console.error("Failed to copy names: ", err)
      toast.error("Failed to copy names")
    }
  }

  return (
    <div className="relative grid gap-4">
      <div className="mt-4 grid gap-2">
        <p className="max-w-md  text-xl font-bold leading-5 sm:text-2xl sm:leading-6">
          {t("which-name-wins-let-your-friends-help-decide")}
        </p>
        <FormLabel className="sr-only">{t("share")}</FormLabel>

        <p className="max-w-md text-sm text-muted-foreground">
          {t("share-on-social-media-to-get-50-tokens")}
        </p>
        <SocialMediaShare
          className="mx-auto mt-4 rounded-full bg-muted px-6 pb-2 pt-3"
          url={url}
          hashtag={translatedSubject}
          title={`${translatedSubject}: ${t("sharedTitle")} ${completion}`}
        />
      </div>
      <Button
        onClick={handleCopyAll}
        disabled={isLoading && !completion}
        type="button"
        variant="outline"
        size="sm"
        className="mx-auto mt-4 h-8"
      >
        <span className="mr-2">{t("copy-all")}</span>
        <CopyIcon className="size-3.5" />
      </Button>
      <div className="grid grid-cols-2 gap-2">
        {Array(boxCount)
          .fill(0)
          .map((_, i) => (
            <div
              key={i}
              className="relative flex h-20 flex-col items-center justify-around border-2 border-dashed border-border bg-muted p-4 text-foreground"
            >
              {names[i] && (
                <CopyButton
                  className="absolute right-1 top-0 p-0"
                  textToCopy={names[i]}
                />
              )}
              <div className="flex items-center justify-center">
                {names[i] || (
                  <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}
